import React, { useMemo, useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import {
  Alert,
  IconButton,
  Link,
  Popover,
  Tile,
  TileContent,
  TileGroup,
  TileStateIndicator,
  Typography
} from '@one-thd/sui-atomic-components';
import { Info } from '@one-thd/sui-icons';
import {
  HAUL_AWAY_TEXT,
  INSTALL_TEXT,
  PROTECTION_TEXT,
  PROTECTION_DETAILS_URL,
  POPOVER_TEXT,
  INSTALL_OPTIONS,
  INSTALL_ITEM_CONFIGURED,
  INSTALL_TWO_STEP,
  INSTALL_FOR_ME,
  INSTALL_FREE,
  INSTALL_SELF,
  DEFAULT_HAUL_AWAY_LABEL
} from '../../constants';
import { getInstallationDetails, getInstallPricing, getDescription } from '../../utils';

const OPTIONS_MAP = {
  protection: { title: PROTECTION_TEXT, popover: POPOVER_TEXT.PROTECTION },
  installation: { title: INSTALL_TEXT, popover: POPOVER_TEXT.INSTALLATION },
  haulAway: { title: HAUL_AWAY_TEXT, popover: POPOVER_TEXT.HAUL_AWAY_W_DOOR },
};

const DECLINE_OPTIONS = {
  protection: { key: 'decline', label: 'Decline Plan', price: null, isDecline: true },
};

const formatPrice = (price) => {
  if (price == null) return '';
  return price === 0 ? 'FREE' : `$${Number(price).toFixed(2)}`;
};
const normalizeOptions = (type, availableItems) => {
  if (!availableItems?.length) return [];

  if (type === 'installation') {
    const details = getInstallationDetails({ addOns: availableItems });
    const pricing = getInstallPricing(availableItems);
    const isHookup = details?.hasHookup;
    const acceptTitle = isHookup ? INSTALL_FREE : INSTALL_FOR_ME;
    const acceptPrice = formatPrice(pricing.totalInstallPrice);
    const declinePrice = formatPrice(pricing.standardKitPrice);

    return [
      {
        key: INSTALL_OPTIONS.ACCEPT,
        label: acceptTitle,
        description: getDescription(pricing.hasRequiredParts, { isAccept: true, isHookup }),
        price: acceptPrice,
      },
      {
        key: INSTALL_OPTIONS.DECLINE,
        label: INSTALL_SELF,
        description: getDescription(pricing.hasRequiredParts, { isAccept: false }),
        price: pricing.hasRequiredParts ? declinePrice : null,
      },
    ];
  }

  let options;

  if (type === 'protection') {
    options = availableItems
      .filter((item) => !!item?.termLength)
      .sort((a, b) => b.termLength - a.termLength)
      .map((item) => {
        const price = Number(item.price || 0);
        return {
          key: item.configAttr || `term-${item.termLength}`,
          label: `${item.termLength}-Year Plan`,
          price: formatPrice(price),
        };
      });
  } else {
    options = availableItems.map((item) => {
      const price = Number(item.price || 0);
      return {
        key: item.configAttr || item.description,
        label: item.description,
        price: formatPrice(price),
      };
    });

    if (type === 'haulAway') {
      const defaultIndex = options.findIndex((option) => option.label === DEFAULT_HAUL_AWAY_LABEL);
      if (defaultIndex > 0) {
        const [defaultOption] = options.splice(defaultIndex, 1);
        options.unshift(defaultOption);
      }
    }
  }

  return type === 'protection' ? [...options, DECLINE_OPTIONS[type]] : options;
};

const getSelectedKey = (type, userSelection, options) => {
  if (!userSelection) return null;

  if (type === 'protection') {
    if (!userSelection?.termLength) return 'decline';
    return options.find((o) =>
      !o.isDecline
      && (o.key === userSelection.configAttr || o.key === `term-${userSelection.termLength}`)
    )?.key || null;
  }

  if (Array.isArray(userSelection)) {
    const hasReal = userSelection.some((item) =>
      item?.installation || item?.configAttr === 'Hookup' || item?.price > 0
    );
    if (!hasReal) return 'decline';
    return userSelection[0]?.configAttr || userSelection[0]?.description || null;
  }

  return null;
};

const getPopoverText = (type, availableItems) => {
  if (type !== 'haulAway') return OPTIONS_MAP[type].popover;

  const hasDoorSwing = availableItems?.some(
    (item) => item?.description?.trim() === 'Change Door Swing Direction'
  );
  return hasDoorSwing ? POPOVER_TEXT.HAUL_AWAY_W_DOOR : POPOVER_TEXT.HAUL_AWAY_WO_DOOR;
};

export const DropDownButton = ({
  type,
  onSelectionChange,
  userSelection,
  availableItems,
  isBopisAndPickup
}) => {
  const isInstallation = type === 'installation';
  const { title } = OPTIONS_MAP[type];
  const popoverText = useMemo(() => getPopoverText(type, availableItems), [type, availableItems]);
  const installDetails = isInstallation ? getInstallationDetails({ addOns: availableItems || [] }) : null;
  const installPricing = isInstallation ? getInstallPricing(availableItems || []) : null;
  const options = useMemo(() => normalizeOptions(type, availableItems), [type, availableItems]);

  const isMultiSelect = type === 'haulAway';
  const defaultHaulAwayKey = useMemo(
    () => options.find((option) => option.label === DEFAULT_HAUL_AWAY_LABEL)?.key || null,
    [options]
  );
  const defaultSelectedKeys = useMemo(
    () => (isMultiSelect && defaultHaulAwayKey ? [defaultHaulAwayKey] : []),
    [isMultiSelect, defaultHaulAwayKey]
  );
  const defaultKey = isInstallation ? INSTALL_OPTIONS.ACCEPT : (options[0]?.key || 'decline');
  const [selectedKey, setSelectedKey] = useState(isMultiSelect ? null : defaultKey);
  const [selectedKeys, setSelectedKeys] = useState(defaultSelectedKeys);
  const hasInitializedMultiSelect = useRef(false);

  const findAvailableItem = (key) => {
    if (type === 'protection') {
      return availableItems.find((i) => i.configAttr === key || `term-${i.termLength}` === key);
    }
    return availableItems.find((i) => (i.configAttr || i.description) === key);
  };

  useEffect(() => {
    if (isMultiSelect) {
      hasInitializedMultiSelect.current = false;
    }
  }, [type, isMultiSelect]);

  useEffect(() => {
    if (isInstallation) {
      if (userSelection) {
        const hasAcceptSignal = userSelection.some(
          (item) => item?.installation || item?.configAttr?.toUpperCase() === 'HOOKUP'
        );
        setSelectedKey(hasAcceptSignal ? INSTALL_OPTIONS.ACCEPT : INSTALL_OPTIONS.DECLINE);
      }
      return;
    }
    if (isMultiSelect) {
      if (userSelection === null || userSelection === undefined) {
        if (!hasInitializedMultiSelect.current && defaultSelectedKeys.length > 0) {
          setSelectedKeys(defaultSelectedKeys);
          hasInitializedMultiSelect.current = true;
        }
      } else {
        const keys = userSelection.map((item) => item?.configAttr || item?.description).filter(Boolean);
        setSelectedKeys(keys);
        hasInitializedMultiSelect.current = true;
      }
    } else {
      if (userSelection === null || userSelection === undefined) return;
      setSelectedKey(getSelectedKey(type, userSelection, options) || defaultKey);
    }
  }, [type, userSelection, options, defaultKey, isMultiSelect, isInstallation, defaultSelectedKeys]);

  useEffect(() => {
    if (!onSelectionChange || userSelection) return;

    if (isInstallation) {
      const acceptValue = availableItems.filter(
        (item) => item?.configAttr !== INSTALL_ITEM_CONFIGURED.configAttr
      );
      onSelectionChange(acceptValue);
      return;
    }

    if (isMultiSelect && defaultHaulAwayKey) {
      const defaultItem = findAvailableItem(defaultHaulAwayKey);
      onSelectionChange(defaultItem ? [defaultItem] : null);
      return;
    }

    if (defaultKey !== 'decline') {
      onSelectionChange(findAvailableItem(defaultKey) || null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  let displayPrice;
  if (isMultiSelect) {
    const totalCents = selectedKeys.reduce((total, key) => {
      const item = findAvailableItem(key);
      const priceInCents = Math.round(Number(item?.price || 0) * 100);
      return total + priceInCents;
    }, 0);
    displayPrice = selectedKeys.length === 0 ? '' : formatPrice(totalCents / 100);
  } else {
    const selectedOption = options.find((option) => option.key === selectedKey);
    displayPrice = selectedOption?.price || '';
  }
  const textColor = isBopisAndPickup ? 'inactive' : undefined;

  const handleTileClick = (key) => {
    if (isBopisAndPickup) return;

    if (isInstallation) {
      if (key === selectedKey) return;
      setSelectedKey(key);
      if (onSelectionChange) {
        const installationValue = key === INSTALL_OPTIONS.ACCEPT
          ? availableItems.filter((item) => item?.configAttr !== INSTALL_ITEM_CONFIGURED.configAttr)
          : [INSTALL_ITEM_CONFIGURED, ...installPricing.installPartItems];
        onSelectionChange(installationValue);
      }
      return;
    }

    if (isMultiSelect) {
      if (key === 'decline') {
        setSelectedKeys([]);
        if (onSelectionChange) onSelectionChange(null);
      } else {
        const newKeys = selectedKeys.includes(key)
          ? selectedKeys.filter((k) => k !== key)
          : [...selectedKeys, key];

        setSelectedKeys(newKeys);
        if (onSelectionChange) {
          const selectedItems = newKeys.map((k) => findAvailableItem(k)).filter(Boolean);
          onSelectionChange(selectedItems.length > 0 ? selectedItems : null);
        }
      }
    } else {
      setSelectedKey(key);
      if (onSelectionChange) {
        onSelectionChange(key === 'decline' ? null : findAvailableItem(key));
      }
    }
  };

  const [popoverAnchor, setPopoverAnchor] = useState(null);
  const isPopoverOpen = Boolean(popoverAnchor);
  const popoverId = isPopoverOpen ? `${type}-info-popover` : undefined;

  const handleInfoClick = (event) => setPopoverAnchor(event.currentTarget);
  const handleInfoClose = () => setPopoverAnchor(null);

  return (
    <div className="sui-w-full sui-flex sui-flex-col sui-gap-2">
      <div className="sui-flex sui-justify-between sui-items-center">
        <div className="sui-flex sui-items-center sui-gap-1">
          <Typography variant="h5" color={textColor}>{title}</Typography>
          <IconButton aria-describedby={popoverId} onClick={handleInfoClick} icon={Info} color="info"/>
          <Popover
            id={popoverId}
            open={isPopoverOpen}
            anchorEl={popoverAnchor}
            onClose={handleInfoClose}
          >
            <Typography variant="body-xs">
              {popoverText}
            </Typography>
            {type === 'protection' && (
              <Link href={PROTECTION_DETAILS_URL} target="_blank" variant="body-xs">
                Plan Details
              </Link>
            )}
          </Popover>
        </div>
        <Typography variant="h5" color={textColor}>
          {displayPrice}
        </Typography>
      </div>
      {isInstallation && installDetails?.hasTwoStepInstallation && (
        <Alert status="info">{INSTALL_TWO_STEP}</Alert>
      )}
      {type === 'haulAway' ? (
        <Typography variant="body-sm" color={textColor} className="sui-whitespace-nowrap">
          * Select all that apply
        </Typography>
      ) : null}
      <TileGroup
        aria-label={`${title} options`}
        value={isMultiSelect ? selectedKeys : selectedKey}
        disableExclusive={isMultiSelect}
        {...(isInstallation
          ? { grow: true }
          : { className: "sui-grid sui-grid-cols-1 sm:sui-grid-cols-3 sui-gap-2" }
        )}
      >
        {options.map((option) => {
          const isTileSelected = isMultiSelect
            ? selectedKeys.includes(option.key)
            : option.key === selectedKey;
          const tileWeight = isTileSelected ? 'bold' : 'regular';

          return (
            <Tile
              key={option.key}
              value={option.key}
              aria-label={option.label}
              disabled={isBopisAndPickup}
              onClick={isBopisAndPickup ? undefined : () => handleTileClick(option.key)}
            >
              <TileContent alignItems="start">
                <div className="sui-flex sui-flex-col sui-gap-2 sui-flex-1">
                  <Typography variant="body-base" weight={tileWeight} color={textColor}>{option.label}</Typography>
                  {option.description && (
                    <Typography variant="body-sm" color={textColor}>{option.description}</Typography>
                  )}
                  {option.isDecline ? (
                    <Typography variant="body-sm" color={textColor}>Not Recommended</Typography>
                  ) : option.price && (
                    <Typography variant="body-base" weight={tileWeight} color={textColor}>{option.price}</Typography>
                  )}
                </div>
                <TileStateIndicator position="top" />
              </TileContent>
            </Tile>
          );
        })}
      </TileGroup>
    </div>
  );
};

DropDownButton.propTypes = {
  onSelectionChange: PropTypes.func,
  type: PropTypes.oneOf(['protection', 'installation', 'haulAway']).isRequired,
  userSelection: PropTypes.oneOfType([
    PropTypes.object,
    PropTypes.arrayOf(PropTypes.object)
  ]),
  availableItems: PropTypes.arrayOf(PropTypes.shape({})),
  isBopisAndPickup: PropTypes.bool
};

DropDownButton.defaultProps = {
  onSelectionChange: null,
  userSelection: null,
  availableItems: [],
  isBopisAndPickup: false
};

DropDownButton.displayName = 'DropDownButton';
